import Flickity from 'flickity';
import FlickityFade from 'flickity-fade';

import debounce from '../util/debounce';

const selectors = {
  slider: '[data-slider]',
  slide: '[data-slide]',
  slideValue: 'data-slide',
  prevArrow: '[data-prev-arrow]',
  nextArrow: '[data-next-arrow]',
  slideshowSlideImg: '.slide-image-img',
  flickityPrevArrow: '.flickity-button.previous',
  flickityNextArrow: '.flickity-button.next',
  collectionImage: '.collection-item__image',
  productItemImage: '.product-item__image',
  columnImage: '.column__image__wrapper',
  sliderThumb: '[data-slider-thumb]',
  sliderThumbClick: '[data-slider-thumb-click]',
  heroContent: '.hero__content',
  heroContentWrapper: '.hero__content__wrapper',
  dataSliderAnimate: 'data-slider-animate',
  dataSliderAnimateOnce: 'data-slider-animate-once',
  dataAos: '[data-aos]',
  dataAspectRatio: 'data-aspectratio',
  dataDots: 'data-dots',
  dataArrows: 'data-arrows',
  dataAutoplay: 'data-autoplay',
  dataAutoplaySpeed: 'data-speed',
  dataColor: 'data-color',
  dataInfinite: 'data-infinite',
  dataSetHeight: 'data-set-height',
  dataWatchCss: 'data-watch-css',
  dataAdaptiveHeight: 'data-adaptive-height',
  dataCellAlign: 'data-cell-align',
  dataDraggable: 'data-draggable',
  dataPercentPosition: 'data-percent-position',
  dataSlideIndex: 'data-slide-index',
  dataSlidesLargeDesktop: 'data-slides-large-desktop',
  dataSlidesDesktop: 'data-slides-desktop',
  dataSlidesTabletDesktop: 'data-slides-tablet',
  dataSlidesMobileDesktop: 'data-slides-mobile',
  dataSliderStartIndex: 'data-slider-start-index',
  dataGroupCells: 'data-group-cells',
  dataArrowPositionMiddle: 'data-arrow-position-middle',
  dataEqualizeHeight: 'data-equalize-height',
  dataFade: 'data-fade',
};

const classes = {
  classIsSelected: 'is-selected',
  textDark: 'text-dark',
  textLight: 'text-light',
  transparentWrapper: 'transparent__wrapper',
  heroContentTransparent: 'hero__content--transparent',
  classSliderInitialized: 'js-slider--initialized',
  classSliderArrowsHidden: 'flickity-button-hide',
  classAosAnimate: 'aos-animate',
  classAosAnimated: 'aos-animated',
};

const sections = {};

class Slider {
  constructor(container, slideshow = null) {
    this.container = container;
    this.slideshow = slideshow || this.container.querySelector(selectors.slider);

    if (!this.slideshow) return;

    this.slideshowSlides = this.slideshow.querySelectorAll(selectors.slide);
    this.sliderPrev = this.container.querySelector(selectors.prevArrow);
    this.sliderNext = this.container.querySelector(selectors.nextArrow);
    this.sliderThumbs = this.container.querySelectorAll(selectors.sliderThumb);
    this.sliderThumbsClick = this.container.querySelectorAll(selectors.sliderThumbClick);
    this.currentSlideColor = this.slideshowSlides[0].getAttribute(selectors.dataColor);
    this.showDots = this.slideshow.getAttribute(selectors.dataDots) != 'hidden';
    this.showArrows = this.slideshow.getAttribute(selectors.dataArrows) === 'true';
    this.autoPlay = this.slideshow.getAttribute(selectors.dataAutoplay) === 'true';
    this.autoPlaySpeed = this.slideshow.getAttribute(selectors.dataAutoplaySpeed);
    this.infinite = this.slideshow.getAttribute(selectors.dataInfinite) !== 'false';
    this.setMinHeightFlag = this.slideshow.getAttribute(selectors.dataSetHeight) === 'true';
    this.watchCss = this.slideshow.getAttribute(selectors.dataWatchCss) === 'true';
    this.adaptiveHeight = this.slideshow.getAttribute(selectors.dataAdaptiveHeight) !== 'false';
    this.cellAlignLeft = this.slideshow.getAttribute(selectors.dataCellAlign) === 'left';
    this.cellAlignRight = this.slideshow.getAttribute(selectors.dataCellAlign) === 'right';
    this.draggable = this.slideshow.getAttribute(selectors.dataDraggable) !== 'false';
    this.percentPosition = this.slideshow.getAttribute(selectors.dataPercentPosition) !== 'false';
    this.multipleSlides = this.slideshow.hasAttribute(selectors.dataSlidesLargeDesktop);
    this.sliderStartIndex = this.slideshow.hasAttribute(selectors.dataSliderStartIndex);
    this.groupCells = this.slideshow.getAttribute(selectors.dataGroupCells) === 'true';
    this.sliderAnimate = this.slideshow.getAttribute(selectors.dataSliderAnimate) === 'true';
    this.sliderAnimateOnce = this.slideshow.getAttribute(selectors.dataSliderAnimateOnce) === 'true';
    this.dataEqualizeHeight = this.slideshow.getAttribute(selectors.dataEqualizeHeight) === 'true';
    this.fade = this.slideshow.getAttribute(selectors.dataFade) === 'true';
    this.resizeEvent = debounce(() => this.resizeEvents(), 100);
    this.resizeEventAlt = debounce(() => this.addRemoveSlidesForDevices(), 100);

    this.flkty = null;

    this.init();
  }

  init() {
    this.setMinHeight();

    const sliderOptions = {
      nitialIndex: this.sliderStartIndex ? parseInt(this.slideshow.getAttribute(selectors.dataSliderStartIndex)) : 0,
      autoPlay: this.autoPlay && this.autoPlaySpeed ? parseInt(this.autoPlaySpeed) : false,
      contain: true,
      pageDots: this.showDots,
      prevNextButtons: this.showArrows,
      adaptiveHeight: this.adaptiveHeight,
      wrapAround: this.infinite,
      percentPosition: this.percentPosition,
      watchCSS: this.watchCss,
      cellAlign: this.cellAlignLeft ? 'left' : this.cellAlignRight ? 'right' : 'center',
      groupCells: this.groupCells,
      draggable: this.draggable ? '>1' : false,
      on: {
        ready: () => {
          if (this.sliderAnimate && this.sliderAnimateOnce && !this.autoPlay) {
            const currentSlide = this.slideshow.querySelector(`.${classes.classIsSelected}`);
            currentSlide.classList.add(classes.classAosAnimated);
          }

          setTimeout(() => {
            this.slideshow.parentNode.dispatchEvent(
              new CustomEvent('slider-is-loaded', {
                bubbles: true,
                detail: {
                  slider: this,
                },
              })
            );
          }, 10);

          this.slideActions();

          if (this.slideshow.classList.contains(classes.classIsSelected)) {
            this.slideshow.classList.remove(classes.classIsSelected);
          }
          if (this.showArrows) {
            this.initArrows();
            this.positionArrows();
          }
        },
        resize: () => {
          this.positionArrows();
        },
      },
    };

    if (this.fade) {
      sliderOptions.fade = true;
      this.flkty = new FlickityFade(this.slideshow, sliderOptions);
    }

    if (!this.fade) {
      this.flkty = new Flickity(this.slideshow, sliderOptions);
    }

    if (this.dataEqualizeHeight) {
      this.equalizeHeight();
    }

    if (this.sliderPrev) {
      this.sliderPrev.addEventListener('click', (e) => {
        e.preventDefault();

        this.flkty.previous(true);
      });
    }

    if (this.sliderNext) {
      this.sliderNext.addEventListener('click', (e) => {
        e.preventDefault();

        this.flkty.next(true);
      });
    }

    this.flkty.on('change', () => this.slideActions());

    this.addRemoveSlidesForDevices();

    window.addEventListener('resize', this.resizeEventAlt);

    if (this.sliderAnimate) {
      this.flkty.on('settle', () => this.sliderSettle());
    }

    if (this.setMinHeightFlag || this.multipleSlides) {
      window.addEventListener('resize', this.resizeEvent);
    }

    if (this.sliderThumbsClick.length) {
      this.sliderThumbsClick.forEach((element) => {
        element.addEventListener('click', (e) => {
          e.preventDefault();
          const slideIndex = [...element.parentElement.children].indexOf(element);
          this.flkty.select(slideIndex);
        });
      });
    }
  }

  sliderSettle() {
    let animatedItems = this.slideshow.querySelectorAll(`.${classes.classIsSelected} ${selectors.dataAos}`);
    if (this.sliderAnimateOnce) {
      animatedItems = this.slideshow.querySelectorAll(`.${classes.classIsSelected}:not(.${classes.classAosAnimated}) .${classes.classAosAnimated}`);
    }
    if (animatedItems.length) {
      animatedItems.forEach((animatedItem) => {
        animatedItem.classList.add(classes.classAosAnimate);
        if (this.sliderAnimateOnce) {
          animatedItem.closest(`.${classes.classIsSelected}`).classList.add(classes.classAosAnimated);
        }
      });
    }
  }

  addRemoveSlidesForDevices() {
    this.hasDiffSlidesForMobileDesktop =
      Array.prototype.filter.call(this.slideshowSlides, (slide) => {
        if (slide.classList.contains('desktop') || slide.classList.contains('mobile')) {
          return slide;
        }
      }).length > 0;

    if (!this.hasDiffSlidesForMobileDesktop) {
      return;
    }

    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    let selectorSlides = null;

    if (windowWidth <= 749) {
      selectorSlides = `${selectors.slide}.mobile, ${selectors.slide}:not(.desktop)`;
    } else {
      selectorSlides = `${selectors.slide}.desktop, ${selectors.slide}:not(.mobile)`;
    }

    this.flkty.options.cellSelector = selectorSlides;
    this.flkty.selectCell(0, false, true);
    this.flkty.reloadCells();
    this.flkty.reposition();
    this.flkty.resize();
    this.slideActions();
  }

  resizeEvents() {
    this.setMinHeight();

    if (this.multipleSlides) {
      if (this.showArrows) {
        this.initArrows();
      }
      this.flkty.resize();
      if (!this.slideshow.classList.contains(classes.classSliderInitialized)) {
        this.flkty.select(0);
      }
    }
  }

  slideActions() {
    const currentSlide = this.slideshow.querySelector(`.${classes.classIsSelected}`);
    this.currentSlideColor = currentSlide.getAttribute(selectors.dataColor);

    if (this.currentSlideColor) {
      this.slideshow.classList.remove(classes.textLight, classes.textDark);
      this.slideshow.classList.add(this.currentSlideColor);
    }

    this.container.classList.remove(classes.transparentWrapper);
    const heroContentWrapper = currentSlide.querySelector(selectors.heroContentWrapper);
    if (heroContentWrapper && heroContentWrapper.classList.contains(classes.heroContentTransparent)) {
      this.container.classList.add(classes.transparentWrapper);
    }

    this.setMinHeight();

    if (this.sliderAnimate) {
      let animatedItems = this.slideshow.querySelectorAll(`.${classes.classIsSelected} .${classes.classAosAnimate}`);
      if (this.sliderAnimateOnce) {
        animatedItems = this.slideshow.querySelectorAll(`.${classes.classIsSelected}:not(.${classes.classAosAnimated}) .${classes.classAosAnimate}`);
      }
      if (animatedItems.length) {
        animatedItems.forEach((animatedItem) => {
          animatedItem.classList.remove(classes.classAosAnimate);
          if (this.sliderAnimateOnce) {
            animatedItem.classList.add(classes.classAosAnimated);
          }
        });
      }
    }

    if (this.sliderThumbs.length && this.sliderThumbs.length === this.slideshowSlides.length && currentSlide.hasAttribute(selectors.dataSlideIndex)) {
      const slideIndex = parseInt(currentSlide.getAttribute(selectors.dataSlideIndex));
      const currentThumb = this.container.querySelector(`${selectors.sliderThumb}.${classes.classIsSelected}`);
      if (currentThumb) {
        currentThumb.classList.remove(classes.classIsSelected);
      }
      this.sliderThumbs[slideIndex].classList.add(classes.classIsSelected);
    }
  }

  setMinHeight() {
    if (!this.setMinHeightFlag) return;
    this.slideshowSlides.forEach((element) => {
      const slideImageImg = element.querySelector(selectors.slideshowSlideImg);
      let slideAspectRatio = '';
      if (slideImageImg && slideImageImg.hasAttribute(selectors.dataAspectRatio)) {
        slideAspectRatio = slideImageImg.getAttribute(selectors.dataAspectRatio);
      }

      let slideTextContentHeight = 0;
      let getMargin = 0;
      const slideTextContent = element.querySelector(selectors.heroContent);
      if (slideTextContent) {
        const getMarginTop = parseInt(window.getComputedStyle(slideTextContent).marginTop);
        const getMarginBottom = parseInt(window.getComputedStyle(slideTextContent).marginBottom);
        getMargin = getMarginTop + getMarginBottom;
        slideTextContentHeight = slideTextContent.offsetHeight + getMargin;
      }

      const slideWidth = parseInt(getComputedStyle(element, null).width.replace('px', ''));
      let slideHeight = parseInt(slideWidth / slideAspectRatio) || 0;
      const isCurrentSlide = element.classList.contains(classes.classIsSelected);

      if (slideTextContentHeight > slideHeight) {
        slideHeight = slideTextContentHeight;
      }

      const minHeightValue = `calc(${slideHeight}px + var(--header-padding)`;
      element.style.setProperty('min-height', minHeightValue);
      const heroContentWrapper = element.querySelector(selectors.heroContentWrapper);
      if (heroContentWrapper) {
        heroContentWrapper.style.setProperty('min-height', minHeightValue);
      }
      if (isCurrentSlide) {
        this.slideshow.parentElement.style.setProperty('min-height', minHeightValue);
      }
    });
  }

  positionArrows() {
    if (this.slideshow.hasAttribute(selectors.dataArrowPositionMiddle) && this.showArrows) {
      const itemImage = this.slideshow.querySelector(selectors.collectionImage) || this.slideshow.querySelector(selectors.productItemImage) || this.slideshow.querySelector(selectors.columnImage);

      this.slideshow.querySelector(selectors.flickityPrevArrow).style.top = itemImage.clientHeight / 2 + 'px';
      this.slideshow.querySelector(selectors.flickityNextArrow).style.top = itemImage.clientHeight / 2 + 'px';
    }
  }

  initArrows() {
    if (!this.multipleSlides) return;
    const slidesNumberCustom = parseInt(this.slideshow.getAttribute(selectors.dataSlidesLargeDesktop));
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const desktopSlides = this.slideshow.hasAttribute(selectors.dataSlidesDesktop) ? parseInt(this.slideshow.getAttribute(selectors.dataSlidesDesktop)) : 3;
    const tabletSlides = this.slideshow.hasAttribute(selectors.dataSlidesTabletDesktop) ? parseInt(this.slideshow.getAttribute(selectors.dataSlidesTabletDesktop)) : 2;
    const mobileSlides = this.slideshow.hasAttribute(selectors.dataSlidesMobileDesktop) ? parseInt(this.slideshow.getAttribute(selectors.dataSlidesMobileDesktop)) : 1;
    const largeDesktopCheck = windowWidth > 1339 && this.slideshowSlides.length > slidesNumberCustom;
    const desktopCheck = windowWidth <= 1339 && windowWidth > 1023 && this.slideshowSlides.length > desktopSlides;
    const tabletCheck = windowWidth <= 1023 && windowWidth > 749 && this.slideshowSlides.length > tabletSlides;
    const mobileCheck = windowWidth <= 749 && this.slideshowSlides.length > mobileSlides;
    const flag = Boolean(largeDesktopCheck || desktopCheck || tabletCheck || mobileCheck);
    this.slideshow.classList.toggle(classes.classSliderArrowsHidden, !flag);
    this.slideshow.classList.toggle(classes.classSliderInitialized, flag);
  }

  equalizeHeight() {
    Flickity.prototype._createResizeClass = function () {
      setTimeout(() => {
        this.element.classList.add('flickity-resize');
      });
    };

    Flickity.createMethods.push('_createResizeClass');

    const resize = Flickity.prototype.resize;
    Flickity.prototype.resize = function () {
      this.element.classList.remove('flickity-resize');
      resize.call(this);
      this.element.classList.add('flickity-resize');
    };

    this.flkty.resize();
  }

  onUnload() {
    if (this.setMinHeightFlag || this.multipleSlides) {
      window.removeEventListener('resize', this.resizeEvent);
    }

    if (this.slideshow && this.flkty) {
      this.flkty.options.watchCSS = false;
      this.flkty.destroy();
    }
  }

  onBlockSelect(evt) {
    if (!this.slideshow) return;
    // Ignore the cloned version
    const slide = this.slideshow.querySelector(`[${selectors.slideValue}="${evt.detail.blockId}"]`);
    if (!slide) return;
    let slideIndex = parseInt(slide.getAttribute(selectors.dataSlideIndex));

    if (this.multipleSlides && !this.slideshow.classList.contains(classes.classSliderInitialized)) {
      slideIndex = 0;
    }

    this.slideshow.classList.add(classes.classIsSelected);

    // Go to selected slide, pause autoplay
    this.flkty.select(slideIndex);
    this.flkty.stopPlayer();
  }

  onBlockDeselect() {
    if (!this.slideshow) return;
    this.slideshow.classList.remove(classes.classIsSelected);

    if (!this.autoPlay) return;
    this.flkty.playPlayer();
  }
}

const slider = {
  onLoad() {
    sections[this.id] = [];
    const els = this.container.querySelectorAll(selectors.slider);
    els.forEach((el) => {
      sections[this.id].push(new Slider(this.container, el));
    });
  },
  onUnload(e) {
    sections[this.id].forEach((el) => {
      if (typeof el.onUnload === 'function') {
        el.onUnload(e);
      }
    });
  },
  onBlockSelect(e) {
    sections[this.id].forEach((el) => {
      if (typeof el.onBlockSelect === 'function') {
        el.onBlockSelect(e);
      }
    });
  },
  onBlockDeselect(e) {
    sections[this.id].forEach((el) => {
      if (typeof el.onBlockDeselect === 'function') {
        el.onBlockDeselect(e);
      }
    });
  },
};

export {slider, Slider};
